import { IRiderDetail } from '../models/IRider';
import ITagRegistration from '../models/ITagRegistration';
import IUnlinkedTagRider from '../models/IUnlinkedTagRider';
import { IUser } from '../models/IUser';
import IUserParticipation from '../models/IUserParticipation';
import IUserTag from '../models/IUserTag';
import { axiosInstance } from './ServiceConstants';
import axios, { CancelToken } from 'axios';

class UserService {
    private buildServiceError(error: unknown, fallbackMessage: string): Error {
        if (axios.isAxiosError(error)) {
            const responseData = error.response?.data as { message?: string; title?: string } | string | undefined;
            if (typeof responseData === 'string' && responseData.trim().length > 0) {
                return new Error(responseData);
            }
            if (responseData && typeof responseData === 'object') {
                return new Error(responseData.message ?? responseData.title ?? fallbackMessage);
            }
        }

        return new Error(fallbackMessage);
    }

    async updateUserProfile(userProfile: IUser ): Promise<IUser> {
        const response = await axiosInstance.put<IUser>(`api/user/me`, userProfile);
        return response.data;
    }

    async getLoggedInUser(cancelToken?: CancelToken): Promise<IUser> {
        const response = await axiosInstance.get<IUser>('api/user/me', { cancelToken });
        return response.data
    }

    async getUserTags(cancelToken?: CancelToken): Promise<IUserTag[]> {
        const response = await axiosInstance.get<Array<IUserTag | string>>('api/user/tags', { cancelToken });
        return response.data.map((tag) =>
            typeof tag === 'string' ? { tagNumber: tag } : tag
        );
    }
    async getUserHistory(cancelToken?: CancelToken): Promise<IUserParticipation[]> {
        const response = await axiosInstance.get<IUserParticipation[]>(`api/user`, { cancelToken });
        return response.data;
    }

    async getUserParticipation(raceId: string): Promise<IRiderDetail> {
        const response = await axiosInstance.get<IRiderDetail>(`api/user/${raceId}`);
        return response.data;
    }

    async registerTag(tagRegistration: ITagRegistration) {
        try {
            await axiosInstance.post('api/User/registerTag', {
                tagNumber: tagRegistration.tagNumber,
                pin: tagRegistration.pin,
                events: tagRegistration.events ?? [],
            });
        } catch (error) {
            throw this.buildServiceError(error, 'Unable to register tag.');
        }
    }

    async getUnlinkedTagRiders(tagId: string, pin: string, cancelToken?: CancelToken): Promise<IUnlinkedTagRider[]> {
        try {
            const response = await axiosInstance.post<IUnlinkedTagRider[]>(
                `api/User/tags/${tagId}/unlinked-riders`,
                { pin },
                { cancelToken }
            );
            return response.data;
        } catch (error) {
            throw this.buildServiceError(error, 'Unable to load unlinked riders for this tag.');
        }
    }

    async unregisterTag(tagId: string): Promise<void> {
        try {
            await axiosInstance.delete(`api/user/tags/${tagId}`);
        } catch (error) {
            throw this.buildServiceError(error, 'Unable to unregister tag.');
        }
    }
}

const UserServiceInstance = new UserService();

export default UserServiceInstance;